"use client";

import { useState, useEffect } from "react";
import type { Params } from "next/dist/server/request/params";
import { useParams } from "next/navigation";
import { type ColumnDef, type Cell_Core, useTable, tableFeatures, createPaginatedRowModel, rowPaginationFeature, globalFilteringFeature, columnFilteringFeature, createFilteredRowModel } from "@tanstack/react-table";
import { CircleIcon, SwordsIcon, CatIcon, SearchIcon, CircleX } from "lucide-react";
import Icon from "@/components/icon";
import TablePagination from "@/components/table-pagination";
import useTableColumnFilters from "@/hooks/use-table-column-filters";
import { formatDateTime } from "@/utils/format";
import cn from "@/utils/cn";
import { preserveHtmlSpaces } from "@/utils/format";
import { type KillfeedLog, killfeed, wipes, getTrackedPlayerColorsById, getTrackedPlayerIds } from "..";
const features = tableFeatures({
  rowPaginationFeature,
  globalFilteringFeature,
  columnFilteringFeature,
  paginatedRowModel: createPaginatedRowModel(),
  filteredRowModel: createFilteredRowModel()
});
export default function KillfeedTable({
  params
}: {
  params: Params;
}) {
  params = useParams() ?? params;
  const wipeIndex = Number(params.wipeId) - 1;
  const trackedPlayerIds = getTrackedPlayerIds(wipeIndex);
  const trackedPlayerColorsById = getTrackedPlayerColorsById(trackedPlayerIds, wipeIndex);
  const columns: ColumnDef<typeof features, KillfeedLog>[] = [{
    id: "d",
    accessorFn: row => formatDateTime(row.date),
    header: "Date",
    cell: info => info.getValue(),
    filterFn: (row_0, _, filterValue) => filterValue === null || row_0.original.pvp === filterValue
  }, {
    id: "k",
    accessorFn: row_1 => row_1.killer?.id && (row_1.pvp ? <div className="flex items-center gap-1.5">
                    {preserveHtmlSpaces(killfeed.playerNames[row_1.killer.id])}
                    {row_1.killer.streak !== undefined && row_1.killer.streak !== 0 && (row_1.killer.streak < 0 ? <span className="rounded px-1 text-xs text-neutral-100 bg-rose-800">{-row_1.killer.streak}</span> : <span className="rounded px-1 text-xs text-neutral-100 bg-blue-500">{row_1.killer.streak + 1}</span>)}
                </div> : <em>{preserveHtmlSpaces(killfeed.objectNames[row_1.killer.id])}</em>),
    header: "Killer",
    cell: info_0 => info_0.renderValue(),
    meta: {
      isPlayerName: true
    },
    filterFn: (row_2, __0, filterValue_0) => Boolean(!filterValue_0 || trackedPlayerIds.includes(row_2.original.victim.id) || row_2.original.pvp && row_2.original.killer?.id && trackedPlayerIds.includes(row_2.original.killer.id))
  }, {
    id: "v",
    accessorFn: row_3 => <div className="flex items-center gap-1.5">
                    {preserveHtmlSpaces(killfeed.playerNames[row_3.victim.id])}
                    {row_3.victim.streak !== undefined && row_3.victim.streak !== 0 && (row_3.victim.streak < 0 ? <span className="rounded px-1 text-xs text-neutral-100 bg-rose-800">{row_3.pvp ? -row_3.victim.streak + 1 : -row_3.victim.streak}</span> : <span className="rounded px-1 text-xs text-neutral-100 bg-blue-500">{row_3.victim.streak}</span>)}
                </div>,
    header: "Victim",
    cell: info_1 => info_1.renderValue(),
    meta: {
      isPlayerName: true
    }
  }, {
    id: "w",
    accessorFn: row_4 => row_4.weaponId ? preserveHtmlSpaces(killfeed.weaponNames[row_4.weaponId]) : <em>{killfeed.messageNames[row_4.messageId as number]}</em>,
    header: "Weapon / Misc.",
    cell: info_2 => info_2.renderValue()
  }];
  const [data, setData] = useState(() => getWipeKillfeedLogs(wipeIndex));
  const {
    columnFilters,
    setColumnFilters,
    getColumnFilter,
    setColumnFilter
  } = useTableColumnFilters([{
    id: "d",
    value: true
  }, {
    id: "k",
    value: true
  }]);
  const [globalFilter, setGlobalFilter] = useState("");
  const table = useTable({
    features,
    columns,
    data,
    initialState: {
      pagination: {
        pageSize: 100,
        pageIndex: 0
      }
    },
    state: {
      globalFilter,
      columnFilters
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    globalFilterFn: filterNames
  });
  useEffect(() => {
    params?.wipeId && setData(getWipeKillfeedLogs(wipeIndex));
  }, [wipeIndex]);
  const pvp = (getColumnFilter("d") ?? null) as boolean | null;
  const setPvp = (value: typeof pvp) => setColumnFilter("d", value);
  const setFilter = (value_0: string) => {
    if (value_0) setColumnFilter("k", false);
    table.setGlobalFilter(value_0);
  };
  return <>
            <div className="h-8 pr-2.5 flex w-full overflow-x-auto whitespace-nowrap">
                <div className="relative flex items-center border-r px-2.5">
                    <Icon component={SearchIcon} className="absolute inset-s-2.5 flex items-center pointer-events-none text-neutral-400" />
                    <input type="text" name="filter" placeholder="Type to filter..." value={table.state.globalFilter ?? ""} onChange={(e: any) => setFilter(e.target.value)} className="px-6 outline-none" />
                    {!!table.state.globalFilter && <button onClick={() => table.resetGlobalFilter()} className="absolute inset-e-2.5 flex items-center text-neutral-400 cursor-pointer hover:text-neutral-100">
                            <Icon component={CircleX} />
                        </button>}
                </div>

                <div className="flex items-center gap-1.5 text-neutral-300">
                    <div className="flex h-full" role="group">
                        <button disabled={pvp === null} onClick={() => setPvp(null)} title="All" className={cn("flex items-center justify-center aspect-square text-center cursor-pointer border-r hover:text-neutral-100 hover:bg-neutral-800", pvp === null && "bg-neutral-900")}>
                            <Icon component={CircleIcon} />
                        </button>

                        <button disabled={pvp === true} onClick={() => setPvp(true)} title="PvP" className={cn("flex items-center justify-center aspect-square text-center cursor-pointer border-r hover:text-neutral-100 hover:bg-neutral-800", pvp === true && "bg-neutral-900")}>
                            <Icon component={SwordsIcon} />
                        </button>

                        <button disabled={pvp === false} onClick={() => setPvp(false)} title="PvE" className={cn("flex items-center justify-center aspect-square text-center cursor-pointer border-r hover:text-neutral-100 hover:bg-neutral-800", pvp === false && "bg-neutral-900")}>
                            <Icon component={CatIcon} />
                        </button>
                    </div>
                </div>

                <label className="flex items-center h-full border-r px-2.5 gap-1.5 cursor-pointer group text-neutral-300 hover:text-neutral-100">
                    <input type="checkbox" checked={getColumnFilter("k") as boolean} name="tracked-players-only" onChange={e_0 => setColumnFilter("k", e_0.target.checked)} className={cn("cursor-pointer", "bg-blue-500 group-hover:bg-blue-300")} />
                    Tracked players only
                </label>
            </div>

            <div className="overflow-x-auto max-w-full 2xl:overflow-x-visible 2xl:max-w-none">
                <table className="w-full relative bg-neutral-950 2xl:table-fixed">
                    <thead className="2xl:sticky 2xl:top-9.75 bg-neutral-900">
                        {table.getHeaderGroups().map(headerGroup => <tr key={headerGroup.id} className="bg-neutral-900">
                                {headerGroup.headers.map((header, i) => <th key={header.id} className={cn("whitespace-nowrap text-left", (header.column.columnDef?.meta as any)?.headerClassName)}>
                                        <div className={cn("h-10 flex items-center p-1.5 border-t border-b-3", !i && "pl-2.5", i === headerGroup.headers.length - 1 && "pr-2.5")}>
                                            {header.isPlaceholder ? null : <table.FlexRender header={header} />}
                                        </div>
                                    </th>)}
                            </tr>)}
                    </thead>

                    <tbody>
                        {table.getRowModel().rows.map(row_5 => <tr key={row_5.id} className={cn("border-b hover:bg-neutral-900/50", !row_5.original.pvp && "text-neutral-500")}>
                                {row_5.getAllCells().map((cell, i_0) => <td key={cell.id} className={cn("whitespace-nowrap p-1.5", !i_0 && "pl-2.5", i_0 === columns.length - 1 && "pr-2.5", row_5.original.ignoredWeapon && cell.column.id === "w" && "bg-rose-900", (cell.column.columnDef?.meta as any)?.isPlayerName && getTrackedPlayerColor(cell, trackedPlayerColorsById))}>
                                        <table.FlexRender cell={cell} />
                                    </td>)}
                            </tr>)}
                    </tbody>
                </table>
            </div>
            
            <TablePagination variant="glued" page={table.state.pagination.pageIndex + 1} pageCount={table.getPageCount()} setPage={(page: number) => table.setPageIndex(page - 1)} />
        </>;
}
function filterNames(row: any, _: string, filterValue: string) {
  if (!filterValue) return true;
  if (row.getValue("d").includes(filterValue)) return true;
  const value = filterValue.toLowerCase();
  if (killfeed.playerNames[row.original.victim.id].toLowerCase().includes(value)) return true;
  if (row.original.killer.id && killfeed[row.original.pvp ? "playerNames" : "objectNames"][row.original.killer.id].toLowerCase().includes(value)) return true;
  if ((row.original.weaponId ? killfeed.weaponNames[row.original.weaponId] : killfeed.messageNames[row.original.messageId]).toLowerCase().includes(value)) return true;
  return false;
}
function getWipeKillfeedLogs(wipeIndex: number) {
  const from = new Date(wipes[wipeIndex].date);
  const to = wipeIndex < wipes.length - 1 ? new Date(wipes[wipeIndex + 1].date) : Infinity;
  return killfeed.logs.filter(log => {
    const date = new Date(log.date);
    return from <= date && date < to;
  });
}
function getTrackedPlayerColor(cell: Cell_Core<any, any>, trackedPlayerColorsById: Record<number, string>) {
  switch (cell.column.id) {
    case "v":
      return cell.row.original.victim.id in trackedPlayerColorsById && trackedPlayerColorsById[cell.row.original.victim.id];
    case "k":
      return cell.row.original.killer && cell.row.original.killer.id in trackedPlayerColorsById && trackedPlayerColorsById[cell.row.original.killer.id];
  }
}